import { stringify } from "qs";
import { CreditNoteStatusType } from "constants/creditNotes";
import { emptyApi } from "./emptyApi";
import { INVOICE_STATUSES_TYPE } from "constants/invoicing";

export type CreateCreditNotePayload = {
  date?: string;
  amount: string;
  description: string;
};

export type LinkedInvoice = {
  uuid: string;
  due_date: boolean;
  invoice_number: string;
  invoice_date?: string;
  status?: INVOICE_STATUSES_TYPE;
  customer_id?: string;
  discount_amount?: number;
  tax_amount?: number;
  due_balance?: number;
  total?: number;
  sub_total?: number;
};

export type CreditNote = {
  uuid: string;
  note_id: string;
  date: string;
  status: CreditNoteStatusType;
  amount: number;
  remaining_balance: number;
  linked_amount: number;
  description: string;
  created_at: string;
  updated_at?: string;
  linked_invoices?: LinkedInvoice[];
};

export type CreditNotesResponse = {
  customer_credit_debit_notes: CreditNote[];
  current_page: number;
  total_pages: number;
  total_count: number;
  per_page: number;
};

export const creditNotesApis = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    getAllCreditNotes: build.query<
      CreditNotesResponse,
      {
        groupId: string;
        entityId: string;
        customerId?: string;
        pageNum?: number;
        limit?: number;
        searchTerm?: string;
        sortCol?: string;
        sortOrder?: "ASC" | "DSC";
        status?: CreditNoteStatusType[];
      }
    >({
      query: ({
        groupId,
        entityId,
        customerId,
        pageNum,
        limit = 20,
        searchTerm,
        sortCol,
        sortOrder,
        status,
      }) => {
        const query = stringify(
          {
            customer_id: customerId || null,
            page_num: pageNum,
            limit,
            search_term: searchTerm || null,
            sort_col: sortCol || null,
            sort_order: sortOrder || null,
            status: status?.join(",") || null,
          },
          { skipNulls: true, addQueryPrefix: true }
        );

        return {
          url: `/api/inkle/invoicing/group/${groupId}/entity/${entityId}/customers/${customerId}/credit-debit-notes/${query}`,
        };
      },
      providesTags: (result) => (result ? [{ type: "CREDIT_NOTES" }] : []),
    }),

    getCreditNote: build.query<
      CreditNote,
      {
        groupId: string;
        entityId: string;
        credit_debit_note_id: string;
        customerId: string;
      }
    >({
      query: ({ groupId, entityId, credit_debit_note_id, customerId }) => ({
        url: `/api/inkle/invoicing/group/${groupId}/entity/${entityId}/customers/${customerId}/credit-debit-note/${credit_debit_note_id}/`,
      }),
      providesTags: (result) => (result ? [{ type: "CREDIT_NOTES" }] : []),
    }),

    createCreditNote: build.mutation<
      CreditNote,
      {
        groupId: string;
        entityId: string;
        customerId: string;
        payload: CreateCreditNotePayload;
      }
    >({
      query: ({ groupId, entityId, payload, customerId }) => ({
        url: `/api/inkle/invoicing/group/${groupId}/entity/${entityId}/customers/${customerId}/credit-debit-note/`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: (result) =>
        result ? [{ type: "CREDIT_NOTES" }, { type: "INVOICES" }] : [],
    }),

    updateCreditNote: build.mutation<
      CreditNote,
      {
        groupId: string;
        entityId: string;
        customerId: string;
        creditNoteId: string;
        payload: Partial<CreateCreditNotePayload>;
      }
    >({
      query: ({ groupId, entityId, customerId, creditNoteId, payload }) => ({
        url: `/api/inkle/invoicing/group/${groupId}/entity/${entityId}/customers/${customerId}/credit-debit-note/${creditNoteId}/`,
        method: "PATCH",
        body: payload,
      }),
      invalidatesTags: (result) =>
        result ? [{ type: "CREDIT_NOTES" }, { type: "INVOICES" }] : [],
    }),

    deleteCreditNote: build.mutation<
      void,
      {
        groupId: string;
        entityId: string;
        customerId: string;
        credit_debit_note_id: string;
      }
    >({
      query: ({ groupId, entityId, credit_debit_note_id, customerId }) => ({
        url: `/api/inkle/invoicing/group/${groupId}/entity/${entityId}/customers/${customerId}/credit-debit-note/${credit_debit_note_id}/`,
        method: "DELETE",
      }),
      invalidatesTags: () => [{ type: "CREDIT_NOTES" }, { type: "INVOICES" }],
    }),

    unlinkCreditNote: build.mutation<
      void,
      {
        groupId: string;
        entityId: string;
        invoiceId: string;
        creditNoteId: string;
      }
    >({
      query: ({ groupId, entityId, invoiceId, creditNoteId }) => ({
        url: `/api/inkle/invoicing/group/${groupId}/entity/${entityId}/invoice/${invoiceId}/unlink-credit-note/`,
        method: "DELETE",
        body: {
          credit_debit_note_id: creditNoteId,
        },
      }),
      invalidatesTags: () => [{ type: "CREDIT_NOTES" }, { type: "INVOICES" }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetAllCreditNotesQuery,
  useLazyGetAllCreditNotesQuery,
  useGetCreditNoteQuery,
  useLazyGetCreditNoteQuery,
  useCreateCreditNoteMutation,
  useUpdateCreditNoteMutation,
  useDeleteCreditNoteMutation,
  useUnlinkCreditNoteMutation,
} = creditNotesApis;
